import React, { useMemo, useState } from 'react';
import type { MasterFlightRecord } from '../../../../utils/types';
import {
  formatDisplayDate,
  formatFrequencyWithUnderscores,
  getDisplayFrequency,
  getDisplayDate,
  formatTime,
  getFrequencyLabel,
  getRouteString,
} from '../../../../utils/types';
import { CollapsiblePanel } from '../../common';
import { findServiceTypeByCode, formatServiceTypeLabel } from '../../../../utils/constants/serviceTypes';
import { useGetServiceTypesQuery } from '../../../../services/redux/api';
import { useTimezone } from '../../../../contexts/TimezoneContext';
import { utcToMYT } from '../../../../utils/timeUtils';
import FlightTakeoffIcon from '@mui/icons-material/FlightTakeoff';
import FlightLandIcon from '@mui/icons-material/FlightLand';
import LinkIcon from '@mui/icons-material/Link';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

interface FinalPreviewTabProps {
  draft: MasterFlightRecord;
}

export function FinalPreviewTab({ draft }: FinalPreviewTabProps) {
  const { data: serviceTypes = [] } = useGetServiceTypesQuery();
  const { timezone } = useTimezone();
  const isMYT = timezone === 'MYT';
  const tzLabel = isMYT ? 'MYT' : 'UTC';
  const displayTime = (utcTime: string) =>
    isMYT ? formatTime(utcToMYT(utcTime)) : formatTime(utcTime);
  const [summaryOpen, setSummaryOpen] = useState(true);
  const [openSectors, setOpenSectors] = useState<boolean[]>(() => draft.stations.map(() => true));

  const routeString = useMemo(() => getRouteString(draft.stations), [draft.stations]);
  const freqDisplay = useMemo(() => formatFrequencyWithUnderscores(getDisplayFrequency(draft.frequency, draft.frequencyMYT, timezone)), [draft.frequency, draft.frequencyMYT, timezone]);
  const freqLabel = useMemo(() => (draft.frequency ? getFrequencyLabel(draft.frequency, draft.freqRate) : ''), [draft.frequency, draft.freqRate]);

  const serviceType = useMemo(() => findServiceTypeByCode(serviceTypes, draft.iataServiceTypeCode), [serviceTypes, draft.iataServiceTypeCode]);
  const serviceTypeDisplay = useMemo(() => {
    if (!draft.iataServiceTypeCode) return '—';
    return serviceType ? formatServiceTypeLabel(serviceType) : draft.iataServiceTypeCode;
  }, [draft.iataServiceTypeCode, serviceType]);

  const sectorsCount = draft.stations?.length || 0;

  const toggleSector = (idx: number) => {
    setOpenSectors(prev => prev.map((v, i) => (i === idx ? !v : v)));
  };

  return (
    <div className="space-y-6">

      {/* Adhoc indicator banner */}
      {draft.isAdhoc && (
        <div className="px-4 py-3 bg-amber-50 border border-amber-200 rounded-lg flex items-center gap-2">
          <span className="px-2 py-0.5 bg-amber-100 text-amber-700 rounded-md text-xs font-bold uppercase">Adhoc</span>
          <span className="text-sm text-amber-800">
            This is an adhoc flight override. It will take precedence over the parent schedule on its operating dates.
          </span>
        </div>
      )}

      {/* ── Summary Card ── */}
      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
        {/* Header - always visible, clickable */}
        <button
          type="button"
          onClick={() => setSummaryOpen(!summaryOpen)}
          className="w-full px-5 py-4 flex items-center justify-between cursor-pointer hover:bg-gray-50 transition-colors text-left"
        >
          <div className="flex items-center gap-3 flex-wrap">
            <h3 className="m-0 text-2xl font-bold text-gray-900 font-mono">
              {draft.flightNo || '—'}
            </h3>
            <span className="px-2.5 py-1 bg-primary-100 text-primary-600 rounded-md text-base font-semibold">
              {draft.flightOwner || '—'}
            </span>
            <span className="px-2.5 py-1 bg-gray-100 text-gray-700 rounded-md text-base font-medium">
              {draft.seasonId || '—'}
            </span>
            {draft.operationalSuffix && (
              <span className="px-2.5 py-1 bg-gray-100 text-gray-600 rounded-md text-sm font-medium">
                Suffix: {draft.operationalSuffix}
              </span>
            )}
            <span className="text-md text-gray-400 ml-1">{routeString}</span>
          </div>

          <span className={`text-gray-400 transition-transform duration-300 ease-in-out flex-shrink-0 ${summaryOpen ? 'rotate-180' : 'rotate-0'}`}>
            <ExpandMoreIcon sx={{ fontSize: 22 }} />
          </span>
        </button>

        {/* Collapsible details */}
        <CollapsiblePanel isOpen={summaryOpen} durationMs={300}>
          <div className="px-5 pb-5 border-t border-gray-100 pt-4">
            <div className="grid grid-cols-2 md:grid-cols-3 gap-y-3 gap-x-6">
              <div>
                <div className="text-xs font-medium text-gray-400 uppercase tracking-wide mb-0.5">Period</div>
                <div className="text-lg font-medium text-gray-900">
                  {formatDisplayDate(getDisplayDate(draft.startDate, draft.stations?.[0]?.std, timezone))} → {formatDisplayDate(getDisplayDate(draft.endDate, draft.stations?.[0]?.std, timezone))}
                </div>
              </div>
              <div>
                <div className="text-xs font-medium text-gray-400 uppercase tracking-wide mb-0.5">Frequency</div>
                <div className="flex items-center gap-2">
                  <span className="font-mono text-lg font-semibold text-gray-900 tracking-wider">
                    {freqDisplay}
                  </span>
                  {freqLabel && (
                    <span className="text-xs font-bold px-2 py-0.5 rounded-md border bg-primary-50 text-primary-700 border-primary-500">
                      {freqLabel}
                    </span>
                  )}
                </div>
              </div>
              <div>
                <div className="text-xs font-medium text-gray-400 uppercase tracking-wide mb-0.5">Service Type</div>
                <div className="text-lg font-medium text-gray-900">{serviceTypeDisplay}</div>
              </div>
              <div>
                <div className="text-xs font-medium text-gray-400 uppercase tracking-wide mb-0.5">Route</div>
                <div className="text-lg font-medium text-gray-900">{routeString}</div>
              </div>
              <div>
                <div className="text-xs font-medium text-gray-400 uppercase tracking-wide mb-0.5">Sectors</div>
                <div className="text-lg font-medium text-gray-900">{sectorsCount}</div>
              </div>
              <div>
                <div className="text-xs font-medium text-gray-400 uppercase tracking-wide mb-0.5">Status</div>
                <div className="text-lg font-medium text-gray-900">{draft.scheduleStatus || '—'}</div>
              </div>
            </div>
          </div>
        </CollapsiblePanel>
      </div>

      {/* ── Sector Timeline ── */}
      <div>
        <div className="text-base font-semibold text-gray-700 mb-3">
          Flight Sectors ({sectorsCount})
        </div>

        <div className="flex flex-col gap-4">
          {draft.stations?.map((station, idx) => {
            const isOpen = openSectors[idx] ?? true;
            return (
              <div key={idx} className="bg-white border border-gray-200 rounded-xl overflow-hidden">
                {/* Sector header - clickable */}
                <button
                  type="button"
                  onClick={() => toggleSector(idx)}
                  className="w-full px-4 py-2.5 bg-gray-50 border-b border-gray-200 flex items-center justify-between cursor-pointer hover:bg-gray-100 transition-colors text-left"
                >
                  <div className="flex items-center gap-3">
                    <span className="px-2.5 py-1 bg-primary-500 text-white rounded text-xs font-bold tracking-wider">
                      SECTOR {idx + 1}
                    </span>
                    <span className="font-mono font-medium text-gray-900">
                      {station.departureStation || '___'} → {station.arrivalStation || '___'}
                    </span>
                    <span className="text-md text-gray-400">
                      {displayTime(station.std)} — {displayTime(station.sta)}
                      <span className="ml-1 text-xs">({tzLabel})</span>
                    </span>
                    {(station.ca ?? 0) > 0 && (
                      <span className="px-2 py-0.5 bg-warning-50 text-warning-700 border border-warning-200 rounded text-xs font-medium">
                        +{station.ca}d
                      </span>
                    )}
                  </div>

                  <span className={`text-gray-400 transition-transform duration-300 ease-in-out flex-shrink-0 ${isOpen ? 'rotate-180' : 'rotate-0'}`}>
                    <ExpandMoreIcon sx={{ fontSize: 20 }} />
                  </span>
                </button>

                {/* Collapsible sector content */}
                <CollapsiblePanel isOpen={isOpen} durationMs={300}>
                  <div className="p-4">
                    <div className="grid grid-cols-2 gap-4">
                      {/* Departure */}
                      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                        <div className="flex items-center gap-2 mb-3">
                          <FlightTakeoffIcon sx={{ fontSize: 18 }} className="text-primary-500" />
                          <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Departure</span>
                        </div>
                        <div className="font-mono text-2xl font-bold text-gray-900 mb-1">
                          {station.departureStation || '___'}
                          {station.passengerTerminalDep && (
                            <span className="text-sm text-gray-400 font-normal ml-2">T{station.passengerTerminalDep}</span>
                          )}
                        </div>
                        <div className="flex items-baseline gap-3">
                          <span className="font-mono text-xl font-semibold text-primary-600">{displayTime(station.std)}</span>
                          <span className="text-xs text-gray-400 font-mono">{tzLabel}</span>
                          {!isMYT && station.utcLocalTimeVariationDep && (
                            <span className="text-xs text-gray-400 font-mono">UTC {station.utcLocalTimeVariationDep}</span>
                          )}
                        </div>
                      </div>

                      {/* Arrival */}
                      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                        <div className="flex items-center gap-2 mb-3">
                          <FlightLandIcon sx={{ fontSize: 18 }} className="text-primary-500" />
                          <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Arrival</span>
                        </div>
                        <div className="font-mono text-2xl font-bold text-gray-900 mb-1">
                          {station.arrivalStation || '___'}
                          {station.passengerTerminalArr && (
                            <span className="text-sm text-gray-400 font-normal ml-2">T{station.passengerTerminalArr}</span>
                          )}
                        </div>
                        <div className="flex items-baseline gap-3">
                          <span className="font-mono text-xl font-semibold text-primary-600">{displayTime(station.sta)}</span>
                          <span className="text-xs text-gray-400 font-mono">{tzLabel}</span>
                          {!isMYT && station.utcLocalTimeVariationArr && (
                            <span className="text-xs text-gray-400 font-mono">UTC {station.utcLocalTimeVariationArr}</span>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Aircraft details row */}
                    <div className="mt-3 flex flex-wrap items-center gap-x-5 gap-y-1 px-1 text-sm text-gray-600">
                      {station.aircraftOwner && (
                        <span>
                          <span className="text-gray-400">Owner</span>{' '}
                          <span className="font-medium text-gray-800">{station.aircraftOwner}</span>
                        </span>
                      )}
                      {station.iataSubTypeCode && (
                        <span>
                          <span className="text-gray-400">Aircraft</span>{' '}
                          <span className="font-medium text-gray-800 font-mono">{station.iataSubTypeCode}</span>
                        </span>
                      )}
                      {station.tailNo && (
                        <span>
                          <span className="text-gray-400">Tail</span>{' '}
                          <span className="font-medium text-gray-800 font-mono">{station.tailNo}</span>
                        </span>
                      )}
                      {station.aircraftConfiguration && (
                        <span>
                          <span className="text-gray-400">Config</span>{' '}
                          <span className="font-medium text-gray-800 font-mono">{station.aircraftConfiguration}</span>
                        </span>
                      )}
                      {station.onwardFlight && (
                        <span>
                          <span className="text-gray-400">Onward</span>{' '}
                          <span className="font-medium text-gray-800 font-mono">{station.onwardFlight}</span>
                        </span>
                      )}
                    </div>

                    {/* Codeshare pills */}
                    {station.codeshareFlightNo && station.codeshareFlightNo.length > 0 && (
                      <div className="mt-3 pt-3 border-t border-gray-100 flex items-center gap-2 flex-wrap">
                        <LinkIcon sx={{ fontSize: 16 }} className="text-blue-600" />
                        <span className="text-xs font-semibold text-gray-500">Codeshare</span>
                        {station.codeshareFlightNo.map((flight, csIdx) => (
                          <span
                            key={csIdx}
                            className="px-2.5 py-1 bg-blue-50 text-blue-700 border border-blue-200 rounded-md text-xs font-mono font-medium"
                          >
                            {flight}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                </CollapsiblePanel>
              </div>
            );
          })}
        </div>
      </div>

      {/* Review hint */}
      <div className="text-center text-sm text-gray-400">
        Review the details above and click Save to confirm
      </div>
    </div>
  );
}
